import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, BedDouble, Ruler } from "lucide-react";
import { formatPrice, statusLabel, typeLabel } from "@/lib/site";

const SpecPill = ({ icon: Icon, label }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-surface-2/80 border border-metallic/70 px-3 py-1 text-xs text-slate-300">
    <Icon size={13} className="text-electric" />
    {label}
  </span>
);

export default function VehicleCard({ vehicle, index = 0 }) {
  const sold = vehicle.status === "salt";
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link
        to={`/fordon/${vehicle.id}`}
        data-testid={`vehicle-card-${vehicle.id}`}
        className="group block relative rounded-2xl overflow-hidden border border-metallic/70 bg-surface hover:border-brand/60 transition-colors"
      >
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={vehicle.images?.[0]}
            alt={vehicle.title}
            loading="lazy"
            className={`h-full w-full object-cover transition-transform duration-700 group-hover:scale-105 ${
              sold ? "grayscale-[0.6] opacity-80" : ""
            }`}
          />
          <span
            data-testid={`vehicle-status-${vehicle.id}`}
            className={`absolute top-4 left-4 rounded-full px-3 py-1 font-mono-brand text-[11px] font-semibold tracking-widest backdrop-blur border ${
              sold
                ? "bg-red-500/15 text-red-400 border-red-400/30"
                : "bg-emerald-500/15 text-emerald-400 border-emerald-400/30"
            }`}
          >
            {statusLabel(vehicle.status).toUpperCase()}
          </span>
          <div
            data-testid={`vehicle-price-${vehicle.id}`}
            className="absolute bottom-4 left-4 rounded-full bg-ink/85 backdrop-blur border border-metallic/70 px-4 py-1.5 font-mono-brand font-semibold text-electric text-sm"
          >
            {formatPrice(vehicle.price)}
          </div>
        </div>

        <div className="p-5">
          <p className="font-mono-brand text-[11px] tracking-[0.2em] text-slate-400 uppercase mb-1.5">
            {typeLabel(vehicle.vtype)} · Årsmodell {vehicle.year}
          </p>
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-display font-bold text-lg leading-snug text-white">{vehicle.title}</h3>
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-metallic text-slate-400 transition-all duration-300 group-hover:bg-brand group-hover:border-brand group-hover:text-white group-hover:rotate-45">
              <ArrowUpRight size={16} />
            </span>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {vehicle.specs?.Sovplatser && <SpecPill icon={BedDouble} label={`${vehicle.specs.Sovplatser} sovplatser`} />}
            {vehicle.specs?.["Längd"] && <SpecPill icon={Ruler} label={vehicle.specs["Längd"]} />}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
